import { useState, useEffect, useCallback } from "react";
import { Toaster, toast } from "sonner";
import { useShoe } from "@/hooks/useShoe";
import { AppHeader } from "@/components/AppHeader";
import { ControlsBar } from "@/components/ControlsBar";
import { KeyboardBar } from "@/components/KeyboardBar";
import { CardGrid } from "@/components/CardGrid";
import { StatsPanel } from "@/components/StatsPanel";
import { DealLog } from "@/components/DealLog";
import { DealSpeed } from "@/components/DealSpeed";
import { ShoeDepth } from "@/components/ShoeDepth";
import { SeoContent } from "@/components/SeoContent";
import { AdSlot } from "@/components/AdSlot";
import { cardId } from "@/lib/deck";
import { playClick } from "@/lib/click";

const THEME_KEY = "card-counter-theme";
const COUNT_KEY = "card-counter-show-count";
const SOUND_KEY = "card-counter-sound";

const SKINS = ["felt", "royal", "sunset"];
const DARKISH = ["dark", "felt", "royal", "sunset"];

const RANK_KEYS = { a: "A", "2": "2", "3": "3", "4": "4", "5": "5", "6": "6", "7": "7", "8": "8", "9": "9", "0": "10", t: "10", j: "J", q: "Q", k: "K" };
const SUIT_KEYS = { s: "spades", h: "hearts", d: "diamonds", c: "clubs" };

function useTheme() {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved) return saved;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    if (SKINS.includes(theme)) root.setAttribute("data-skin", theme);
    else root.removeAttribute("data-skin");
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);
  return [theme, setTheme];
}

export default function CardCounter() {
  const { deckCount, counts, history, changeDeckCount, dealCard, undo, reset, stats } = useShoe();
  const [theme, setTheme] = useTheme();
  const [showCount, setShowCount] = useState(() => localStorage.getItem(COUNT_KEY) === "true");
  const [activeSuit, setActiveSuit] = useState("spades");
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem(SOUND_KEY) !== "false");
  const [dealTimes, setDealTimes] = useState([]);

  useEffect(() => {
    localStorage.setItem(COUNT_KEY, String(showCount));
  }, [showCount]);

  useEffect(() => {
    localStorage.setItem(SOUND_KEY, String(soundOn));
  }, [soundOn]);

  const handleDeal = useCallback((id) => {
    if ((counts[id] ?? 0) > 0) {
      if (soundOn) playClick();
      setDealTimes((prev) => [...prev, Date.now()]);
    }
    dealCard(id);
  }, [counts, soundOn, dealCard]);

  const doUndo = useCallback(() => {
    if (history.length === 0) return false;
    undo();
    setDealTimes((prev) => prev.slice(0, -1));
    return true;
  }, [history.length, undo]);

  const handleReset = useCallback(() => {
    reset();
    setDealTimes([]);
    toast.success("Shoe reset", { description: `${deckCount} deck${deckCount > 1 ? "s" : ""} restored to full.` });
  }, [reset, deckCount]);

  const handleUndo = useCallback(() => {
    if (!doUndo()) toast.error("Nothing to undo");
  }, [doUndo]);

  const handleDeckChange = useCallback((val) => {
    const n = Number(val);
    changeDeckCount(n);
    setDealTimes([]);
    toast.success(`${n} deck${n > 1 ? "s" : ""} loaded`, { description: "Fresh shoe dealt." });
  }, [changeDeckCount]);

  useEffect(() => {
    const handler = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target.isContentEditable) return;
      const key = e.key.toLowerCase();
      if (SUIT_KEYS[key]) {
        setActiveSuit(SUIT_KEYS[key]);
        e.preventDefault();
        return;
      }
      if (key === "u") {
        doUndo();
        e.preventDefault();
        return;
      }
      if (RANK_KEYS[key] !== undefined) {
        handleDeal(cardId(activeSuit, RANK_KEYS[key]));
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [activeSuit, handleDeal, doUndo]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster position="top-center" theme={DARKISH.includes(theme) ? "dark" : "light"} richColors />

      <AppHeader theme={theme} setTheme={setTheme} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <ControlsBar
          deckCount={deckCount}
          historyLength={history.length}
          showCount={showCount}
          setShowCount={setShowCount}
          soundOn={soundOn}
          setSoundOn={setSoundOn}
          onDeckChange={handleDeckChange}
          onUndo={handleUndo}
          onReset={handleReset}
        />

        <KeyboardBar activeSuit={activeSuit} setActiveSuit={setActiveSuit} />

        {/* Shoe depth timeline */}
        <ShoeDepth remaining={stats.totalRemaining} total={stats.totalCards} />

        {/* Main layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <section className="lg:col-span-8" aria-label="Card grid">
            <CardGrid deckCount={deckCount} counts={counts} onDeal={handleDeal} />
          </section>

          <aside className="lg:col-span-4 space-y-4" aria-label="Statistics">
            <StatsPanel stats={stats} showCount={showCount} />
            <DealSpeed dealTimes={dealTimes} />
            <DealLog history={history} />
            <AdSlot className="h-48 w-full" />
          </aside>
        </div>

        <AdSlot className="h-24 w-full mt-6" />

        <SeoContent />

        <footer className="mt-8 pt-6 border-t-2 border-foreground text-xs text-muted-foreground font-mono">
          Fully client-side · Your session is saved in this browser only · No accounts, no tracking.
        </footer>
      </main>
    </div>
  );
}
